"use client";

/**
 * A season's table: `EX-INS01`, `docs/29` §6.25, the canvas board
 * `SeasonBar.dc.html` (its bottom half).
 *
 * The rule in words, a chip per round (done, frozen, to come), and the table:
 * each entry's points per round and the total. A round an entry's best
 * `bestOf` left out is drawn grey and is not in the total; a round still to
 * come is a dash. The server folds every number from each round's board under
 * its own cap, so a frozen round's places are the frozen board's, and the
 * table says they may move.
 */
import Link from "next/link";
import { useTranslations } from "next-intl";
import { Snowflake } from "lucide-react";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { formatNumber } from "@/lib/format-datetime";
import { cn } from "@/lib/utils";
import { useContestDisplayZone } from "../../lib/display-zone";
import { formatDateTime } from "../../lib/format";
import { roundLabel } from "../../lib/season";
import type { PublicSeason, SeasonPoints, SeasonRound } from "../../types";

/** "100, 80, 65, 55, 50, then one less each place." in the reader's digits. */
export function usePointsRule() {
  const t = useTranslations("contest.season");
  return (points: SeasonPoints, locale: string) => {
    const list = points.table.map((value) => formatNumber(value, locale)).join(", ");
    if (points.thenMinus === 0) return t("rule.tableOnly", { list });
    if (points.thenMinus === 1) return t("rule.thenOne", { list });
    return t("rule.thenMinus", { list, step: formatNumber(points.thenMinus, locale) });
  };
}

const CHIP: Readonly<Record<NonNullable<SeasonRound["status"]>, string>> = {
  final: "border-emerald-200 bg-emerald-50 text-emerald-700",
  frozen: "border-sky-200 bg-sky-50 text-sky-800",
  upcoming: "border-neutral-200 bg-neutral-50 text-neutral-600",
};

export function SeasonTable({
  season,
  locale,
  heading = "h2",
  highlightContestId,
}: {
  season: Pick<PublicSeason, "title_en" | "title_bn" | "description_en" | "description_bn" | "participation" | "points" | "bestOf" | "rounds" | "rows" | "provisional">;
  locale: string;
  /** The season's own page is its `h1`; inside a tab it is a section's `h2`. */
  heading?: "h1" | "h2";
  /** The contest page's own round, ringed in the chips. */
  highlightContestId?: string;
}) {
  const t = useTranslations("contest.season");
  const rule = usePointsRule();
  const zone = useContestDisplayZone();
  const n = (value: number) => formatNumber(value, locale);
  const Heading = heading;
  const title = locale === "bn" && season.title_bn ? season.title_bn : season.title_en;
  const description = locale === "bn" ? season.description_bn || season.description_en : season.description_en;
  const label = (round: SeasonRound, index: number) => roundLabel(round, index, locale, (number) => t("roundN", { n: n(number) }));
  const team = season.participation === "team";
  const rounds = season.rounds;

  return (
    <section
      aria-label={title}
      className="flex flex-col gap-4 rounded-2xl border border-[rgb(var(--border))] bg-white p-5 shadow-card sm:p-6"
      data-testid="season-table"
    >
      <div className="flex flex-wrap items-end justify-between gap-3.5">
        <div className="flex min-w-0 flex-col gap-1.5">
          <Heading className="font-heading text-[21px] font-bold text-neutral-900">{title}</Heading>
          <p className="text-sm text-neutral-500" data-testid="season-rule">
            {season.bestOf !== null
              ? t("rule.bestOf", { rounds: n(rounds.length), best: n(season.bestOf) })
              : t("rule.every", { rounds: n(rounds.length) })}{" "}
            {rule(season.points, locale)}
          </p>
          {description ? <p className="max-w-[var(--w-read)] text-sm text-neutral-700">{description}</p> : null}
        </div>
        <ul className="flex flex-wrap gap-1.5" aria-label={t("roundsLabel")}>
          {rounds.map((round, index) => (
            <li key={round.contestId}>
              {round.slug ? (
                <Link
                  href={`/${locale}/contests/${round.slug}`}
                  className={cn(
                    "inline-flex h-7 items-center gap-1.5 rounded-full border px-3 text-[12.5px] font-medium transition-colors hover:border-neutral-400",
                    CHIP[round.status ?? "upcoming"],
                    highlightContestId === round.contestId && "ring-2 ring-primary/40",
                  )}
                  data-testid={`season-chip-${round.status ?? "refused"}`}
                  title={[(locale === "bn" && round.title_bn) || round.title_en, round.startAt ? formatDateTime(round.startAt, locale, zone) : null]
                    .filter(Boolean)
                    .join(" · ")}
                >
                  {round.status === "frozen" ? <Snowflake className="h-3 w-3" aria-hidden /> : null}
                  {label(round, index)}
                  <span className="sr-only">{t(`status_${round.status ?? "upcoming"}`)}</span>
                </Link>
              ) : (
                <span className={cn("inline-flex h-7 items-center rounded-full border px-3 text-[12.5px] font-medium", CHIP.upcoming)}>
                  {label(round, index)}
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>

      {season.provisional ? (
        <p className="flex items-start gap-2 rounded-xl border border-sky-200 bg-sky-50 px-3.5 py-2.5 text-[13px] text-sky-900" data-testid="season-provisional">
          <Snowflake className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          {t("provisional")}
        </p>
      ) : null}

      {season.rows.length === 0 ? (
        <p className="rounded-xl border border-dashed border-neutral-200 px-4 py-8 text-center text-sm text-neutral-500" data-testid="season-empty">
          {t("empty")}
        </p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-neutral-100">
          <Table className="text-[14.5px]">
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">{t("rank")}</TableHead>
                <TableHead>{t(team ? "team" : "contestant")}</TableHead>
                <TableHead>{t("institution")}</TableHead>
                {rounds.map((round, index) => (
                  <TableHead key={round.contestId} className="text-right">
                    {label(round, index)}
                  </TableHead>
                ))}
                <TableHead className="text-right">{t("points")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {season.rows.map((row, rowIndex) => (
                <TableRow
                  key={`${row.rank}-${row.name ?? ""}-${rowIndex}`}
                  className={cn("transition-colors hover:bg-neutral-50/70", row.rank <= 3 && "bg-[#FFFCF5]")}
                  data-testid="season-row"
                >
                  <TableCell className="tabular-nums text-neutral-500">{n(row.rank)}</TableCell>
                  <TableCell className="font-medium text-neutral-900">{row.name ?? t("unnamed")}</TableCell>
                  <TableCell className="text-neutral-600">{row.institution ?? ""}</TableCell>
                  {row.cells.map((cell, index) => (
                    <TableCell
                      key={rounds[index]?.contestId ?? index}
                      className={cn("text-right tabular-nums", cell && !cell.counted ? "text-neutral-400" : "text-neutral-900")}
                      title={cell ? t("cellTitle", { place: n(cell.place) }) : undefined}
                      data-testid={cell && !cell.counted ? "season-cell-dropped" : undefined}
                    >
                      {cell ? (
                        <>
                          {n(cell.points)}
                          {!cell.counted ? <span className="sr-only"> {t("dropped")}</span> : null}
                        </>
                      ) : (
                        <span className="text-neutral-300" aria-label={t("noPlace")}>
                          –
                        </span>
                      )}
                    </TableCell>
                  ))}
                  <TableCell className="text-right font-bold tabular-nums text-neutral-900">{n(row.total)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {season.bestOf !== null && season.rows.length > 0 ? (
        <p className="text-[12.5px] text-neutral-500">{t("droppedNote")}</p>
      ) : null}
    </section>
  );
}
